import React, { useRef, useEffect, useState } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import { Icons } from '../../../shared/icons';

export default function SignatureCapture({ 
  onSave, 
  onCancel,
  isMobile = false 
}) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = isMobile ? 3 : 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const getCoordinates = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      };
    };

    const startDrawing = (e) => {
      e.preventDefault();
      setIsDrawing(true);
      const { x, y } = getCoordinates(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const draw = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      const { x, y } = getCoordinates(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      setIsEmpty(false);
    };

    const stopDrawing = () => {
      setIsDrawing(false);
    };

    // Touch events
    canvas.addEventListener('touchstart', startDrawing);
    canvas.addEventListener('touchmove', draw);
    canvas.addEventListener('touchend', stopDrawing);
    
    // Mouse events (for desktop)
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseleave', stopDrawing);

    return () => {
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseleave', stopDrawing);
    };
  }, [isMobile]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  // Export the signature as a downscaled PNG data URL (same policy as
  // SignaturePad): engineer_signatures.signature_data caps at 100k chars
  // after migration 018, and a full-width canvas PNG routinely exceeds the
  // old 5000 cap — the certificate-wizard save path 400'd on create.
  const exportSignature = () => {
    const canvas = canvasRef.current;
    const MAX_W = 600;
    if (canvas.width <= MAX_W) return canvas.toDataURL('image/png');
    const scale = MAX_W / canvas.width;
    const out = document.createElement('canvas');
    out.width = MAX_W;
    out.height = Math.max(1, Math.round(canvas.height * scale));
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return out.toDataURL('image/png');
  };

  const handleSave = () => {
    if (isEmpty) return;
    const dataUrl = exportSignature();
    onSave(dataUrl);
  };

  const containerClass = isMobile ? 'signature-overlay-mobile' : 'signature-container-desktop';

  return (
    <div className={containerClass}>
      {isMobile && (
        <div className="signature-header">
          <h2>Engineer Signature</h2>
          <p className="field-hint">Sign within the box below</p>
        </div>
      )}

      <div className="signature-canvas-wrapper">
        <canvas
          ref={canvasRef}
          className="signature-canvas"
          aria-label="Signature canvas - draw your signature here"
        />
      </div>

      <div className="signature-actions">
        <button
          type="button"
          className="secondary-button"
          onClick={clearCanvas}
          disabled={isEmpty}
          aria-label="Clear signature"
        >
          <SafeIcon icon={Icons.RefreshCw} size={16} />
          Clear
        </button>
        
        {!isMobile && (
          <button
            type="button"
            className="secondary-button"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}

        <button
          type="button"
          className="primary-button"
          onClick={handleSave}
          disabled={isEmpty}
          aria-label="Save signature"
        >
          <SafeIcon icon={Icons.Check} size={16} />
          {isMobile ? 'Done' : 'Save Signature'}
        </button>
      </div>

      {isMobile && (
        <button
          type="button"
          className="icon-button signature-cancel"
          onClick={onCancel}
          aria-label="Cancel signature capture"
        >
          <SafeIcon icon={Icons.X} size={24} />
        </button>
      )}

      <p className="field-hint signature-hint">
        <SafeIcon icon={Icons.Info} size={14} />
        Your signature will be attached to the certificate
      </p>
    </div>
  );
}